import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUserCompany } from '../hooks/useUserCompany';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOnboarding?: boolean; // Optional: allow bypassing onboarding check for specific routes
  requireSubscription?: boolean; // Optional: allow bypassing subscription check (e.g., for checkout page)
}

export default function ProtectedRoute({
  children,
  requireOnboarding = true,
  requireSubscription = true,
}: ProtectedRouteProps) {
  const { user, isLoading: authLoading } = useAuth();
  const { company, isLoading: companyLoading } = useUserCompany();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Show loading spinner while checking auth or company
  if (authLoading || companyLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to onboarding if required and not completed
  if (requireOnboarding && location.pathname !== '/onboarding') {
    // If company data hasn't loaded yet, keep showing spinner (safety net for race conditions)
    if (!company) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      );
    }

    // Company loaded - check onboarding status
    if (!company.onboarding_completed) {
      return <Navigate to="/onboarding" replace />;
    }
  }

  // Check subscription status (after onboarding check)
  if (requireSubscription && company) {
    const hasAccess = checkSubscriptionAccess(company);

    // Allow grace period for checkout success (webhook processing)
    const isCheckoutSuccess = searchParams.get('checkout') === 'success';

    console.log('[ProtectedRoute] 🔍 DETAILED Subscription check:', {
      path: location.pathname,
      hasAccess,
      isCheckoutSuccess,
      subscription_status: company.subscription_status,
      subscription_plan: company.subscription_plan,
      trial_start: company.trial_start,
      trial_end: company.trial_end,
      trial_end_parsed: company.trial_end ? new Date(company.trial_end).toISOString() : null,
      trial_end_vs_now: company.trial_end ? {
        trial_end_date: new Date(company.trial_end).toISOString(),
        now: new Date().toISOString(),
        is_future: new Date(company.trial_end) > new Date(),
        days_remaining: company.trial_end ? Math.ceil((new Date(company.trial_end).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : null,
      } : null,
      stripe_subscription_id: company.stripe_subscription_id,
      current_period_end: company.current_period_end,
    });

    if (!hasAccess && location.pathname !== '/checkout' && !isCheckoutSuccess) {
      console.error('[ProtectedRoute] ❌ ACCESS DENIED - redirecting to /checkout');
      console.error('[ProtectedRoute] Failure reason:', {
        hasAccess: false,
        notOnCheckoutPage: location.pathname !== '/checkout',
        noCheckoutSuccessParam: !isCheckoutSuccess,
        subscription_status: company.subscription_status,
        trial_end: company.trial_end,
      });
      return <Navigate to="/checkout" replace />;
    }

    if (isCheckoutSuccess) {
      console.log('[ProtectedRoute] ✓ Grace period active (checkout=success)');
    } else if (hasAccess) {
      console.log('[ProtectedRoute] ✅ Access granted - valid subscription/trial');
    }
  }

  return <>{children}</>;
}

// Helper function to check if user has valid subscription or trial
function checkSubscriptionAccess(company: any): boolean {
  const { subscription_status, trial_end, current_period_end } = company;

  console.log('[checkSubscriptionAccess] Evaluating access:', {
    subscription_status,
    trial_end,
    current_period_end,
  });

  // Active subscription = access granted
  if (subscription_status === 'active') {
    console.log('[checkSubscriptionAccess] ✅ Access granted: active subscription');
    return true;
  }

  // Trialing with time remaining = access granted
  if (subscription_status === 'trialing') {
    if (!trial_end) {
      console.warn('[checkSubscriptionAccess] ⚠️ ISSUE: trialing status but trial_end is null');
      console.warn('[checkSubscriptionAccess] This suggests a webhook sync issue');
      // TEMPORARY FIX: If they have a trialing status but null trial_end,
      // grant access anyway (webhook issue from Section 141.3)
      // Check if they have a Stripe subscription ID as proof of valid subscription
      if (company.stripe_subscription_id) {
        console.warn('[checkSubscriptionAccess] ✅ Granting access anyway (has stripe_subscription_id)');
        return true;
      } else {
        console.error('[checkSubscriptionAccess] ❌ No stripe_subscription_id, denying access');
        return false;
      }
    }

    const trialEndDate = new Date(trial_end);
    const now = new Date();
    const isValid = trialEndDate > now;

    if (isValid) {
      console.log('[checkSubscriptionAccess] ✅ Access granted: valid trial period');
    } else {
      console.log('[checkSubscriptionAccess] ❌ Trial expired:', {
        trial_end: trialEndDate.toISOString(),
        now: now.toISOString(),
      });
    }

    return isValid;
  }

  // past_due: allow access within the 7-day grace period
  if (subscription_status === 'past_due') {
    const { payment_failed_at } = company;

    // No failure timestamp recorded yet — grant access (first failure just happened)
    if (!payment_failed_at) {
      console.warn('[checkSubscriptionAccess] ⚠️ past_due with no payment_failed_at — granting access');
      return true;
    }

    const failedDate = new Date(payment_failed_at);
    const daysSinceFailed = (Date.now() - failedDate.getTime()) / (1000 * 60 * 60 * 24);
    const GRACE_PERIOD_DAYS = 7;

    if (daysSinceFailed < GRACE_PERIOD_DAYS) {
      console.warn(`[checkSubscriptionAccess] ⚠️ past_due — ${Math.floor(daysSinceFailed)} days elapsed, within grace period`);
      return true;
    }

    console.error(`[checkSubscriptionAccess] ❌ past_due grace period expired: ${Math.floor(daysSinceFailed)} days since failure`);
    return false;
  }

  // All other statuses = no access
  console.log('[checkSubscriptionAccess] ❌ Access denied: invalid status', subscription_status);
  return false;
}
